// Pack 12 · Forgot Password page. Always shows the same success message
// (backend enforces email-enumeration defence) so a hostile visitor cannot
// probe whether an address is registered.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { CheckCircle2, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Rate limiter returns a plain string; validation returns an array.
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <Card className="card-elevated w-full max-w-md p-8" data-testid="forgot-password-card">
        <div className="overline"><span className="accent-line" /> Password recovery</div>
        <h1 className="font-display text-3xl mt-3">Reset your password</h1>

        {sent ? (
          <div className="mt-7 text-sm" data-testid="forgot-password-sent">
            <div className="flex items-start gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-300">Check your email</p>
                <p className="text-muted-foreground mt-1">
                  If <span className="text-foreground">{email}</span> is registered, a reset link is on
                  its way. It expires in 60 minutes.
                </p>
              </div>
            </div>
            <div className="text-center mt-6">
              <Link to="/login" className="text-accent hover:underline text-sm" data-testid="forgot-back-to-login">
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mt-4">
              Enter your account email and we&rsquo;ll send you a secure link to choose a new password.
            </p>
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@firm.com"
                  className="mt-2"
                />
              </div>
              {error && (
                <div className="text-sm text-destructive" data-testid="forgot-error">{error}</div>
              )}
              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={busy || !email}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <Mail className="w-4 h-4 mr-2" />
                {busy ? "Sending link…" : "Send reset link"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              Remembered it? <Link to="/login" className="text-accent hover:underline" data-testid="forgot-back-link">Back to sign in</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
